import {
  BannerSharedItem,
  Block,
  BlockEditorData,
} from "@babackapp/blocks-editor/types";
import {
  EmailsThemeDto,
  UpsertEmailTemplateRequestDto,
} from "@babackapp/private-api-client/types";
import type { EmailTemplateDomain } from "@babackapp/private-api-client/client/use-get-all-shop-email-templates";
import { EmailTemplateFormData } from "../types";

export type PrepareUpsertParams = {
  data: Omit<EmailTemplateFormData, "theme"> & {
    theme?: Required<EmailsThemeDto>;
    category?: string | null;
    domain?: EmailTemplateDomain;
  };
  emailTemplateId?: string | null;
};

export type PreparedUpsertRequest = {
  body: FormData;
  hasUploads: boolean;
};

export function prepareUpsertRequest({
  data,
  emailTemplateId,
}: PrepareUpsertParams): PreparedUpsertRequest {
  const uploadsMap = data.assets?.uploads ?? {};
  const bannerUploads: { key: string; file: File }[] = [];
  const inlineUploads: { key: string; file: File }[] = [];

  const cloneContent = cloneBlockEditorData(data.content);

  const sanitizeBlocks = (blocks: Block[]): Block[] =>
    blocks
      .filter((block) => block !== null && block !== undefined)
      .map((block) => {
        // Handle banner blocks
        if (block.type === "banner") {
          const bannerBlock = block as Block<"banner">;
          const rawProperties = (bannerBlock.properties ?? {}) as {
            layout?: "single" | "double";
            items?: BannerSharedItem[];
          };

          const sanitizedPropertyItems = (rawProperties.items ?? []).map(
            (item) => {
              const result: BannerSharedItem = {
                href: item?.href ?? "",
                imageUrl: item?.imageUrl ?? null,
                imageId: item?.imageId ?? null,
                imageUploadKey: item?.imageUploadKey ?? null,
              };

              if (result.imageUrl && result.imageUrl.startsWith("blob:")) {
                result.imageUrl = null;
              }

              if (result.imageUploadKey) {
                const upload = uploadsMap[result.imageUploadKey];

                if (upload instanceof File) {
                  bannerUploads.push({
                    key: result.imageUploadKey,
                    file: upload,
                  });
                } else {
                  result.imageUploadKey = null;
                }
              }

              return result;
            },
          );

          const sanitizedProperties = {
            ...(bannerBlock.properties ?? {}),
            layout: rawProperties.layout ?? "double",
            items: sanitizedPropertyItems,
          } as Block<"banner">["properties"];

          return {
            ...bannerBlock,
            properties: sanitizedProperties,
            content: undefined,
          };
        }

        // Handle paragraph blocks - rewrite inline images (data-upload-key) so
        // their blob: preview src becomes an `upload://<key>` sentinel the
        // backend resolves to the hosted S3 URL.
        if (block.type === "paragraph") {
          const paragraphContent = block.content as
            | Record<string, { html?: string } | undefined>
            | undefined;

          if (paragraphContent) {
            const rewrittenContent: Record<string, { html?: string }> = {};

            for (const [contentLocale, value] of Object.entries(
              paragraphContent,
            )) {
              if (value && typeof value.html === "string") {
                const { html, keys } = rewriteInlineImageUploads(
                  value.html,
                  uploadsMap,
                );

                keys.forEach((key) => {
                  const upload = uploadsMap[key];
                  if (upload instanceof File) {
                    inlineUploads.push({ key, file: upload });
                  }
                });

                rewrittenContent[contentLocale] = { ...value, html };
              } else if (value) {
                rewrittenContent[contentLocale] = value;
              }
            }

            return {
              ...block,
              content: rewrittenContent,
            } as Block;
          }
        }

        // Handle row blocks - recursively sanitize children
        if (block.type === "row") {
          const rowBlock = block as Block<"row">;
          const content = rowBlock.content as
            | { children?: Block[] }
            | undefined;

          if (content?.children) {
            return {
              ...rowBlock,
              content: {
                ...content,
                children: sanitizeBlocks(content.children),
              },
            };
          }
        }

        // Other block types - return as is
        return block;
      });

  const sanitizedContent: BlockEditorData = {
    ...cloneContent,
    beforeBlocks: sanitizeBlocks(cloneContent.beforeBlocks ?? []),
    insideBlocks: sanitizeBlocks(cloneContent.insideBlocks ?? []),
    afterBlocks: sanitizeBlocks(cloneContent.afterBlocks ?? []),
  };

  // Merge banner + inline image uploads, keeping a single File per key.
  const filesByKey = new Map<string, File>();
  [...bannerUploads, ...inlineUploads].forEach(({ key, file }) => {
    if (!filesByKey.has(key)) {
      filesByKey.set(key, file);
    }
  });
  const uniqueUploadKeys = Array.from(filesByKey.keys());

  const uploadsForAssets: Record<string, null> = {};
  uniqueUploadKeys.forEach((key) => {
    uploadsForAssets[key] = null;
  });

  const assetsPayload =
    uniqueUploadKeys.length > 0
      ? {
          uploads: uploadsForAssets,
        }
      : undefined;

  const templatePayload: NonNullable<
    UpsertEmailTemplateRequestDto["template"]
  > = {
    title: data?.title || "Nouveau template",
    subject: data.subject,
    preview: data.preview,
    content: sanitizedContent,
    type: data.type,
    category: data.category,
    domain: data.domain ?? "tracking",
  };

  const formData = new FormData();

  if (emailTemplateId) {
    formData.append("templateId", emailTemplateId);
  }
  formData.append("template", JSON.stringify(templatePayload));
  if (assetsPayload) {
    formData.append("assets", JSON.stringify(assetsPayload));
  }
  filesByKey.forEach((file, key) => {
    formData.append(`uploads[${key}]`, file);
  });

  return { body: formData, hasUploads: filesByKey.size > 0 };
}

const INLINE_IMAGE_UPLOAD_PREFIX = "upload://";

/**
 * Rewrites inline <img> tags carrying a `data-upload-key` backed by a pending
 * File: swaps their (blob:) src for an `upload://<key>` sentinel that the
 * backend resolves to the hosted S3 URL. Images without a pending File (e.g.
 * already uploaded on a previous save) are left untouched.
 *
 * Returns the rewritten html and the upload keys it touched.
 */
function rewriteInlineImageUploads(
  html: string,
  uploadsMap: Record<string, File | null>,
): { html: string; keys: string[] } {
  const keys: string[] = [];

  const rewrittenHtml = html.replace(/<img\b[^>]*>/gi, (tag) => {
    const keyMatch = tag.match(/data-upload-key="([^"]+)"/);
    const uploadKey = keyMatch?.[1];

    if (!uploadKey || !(uploadsMap[uploadKey] instanceof File)) {
      return tag;
    }

    keys.push(uploadKey);
    const sentinel = `${INLINE_IMAGE_UPLOAD_PREFIX}${uploadKey}`;

    if (/\bsrc="[^"]*"/i.test(tag)) {
      return tag.replace(/(\bsrc=")[^"]*(")/i, `$1${sentinel}$2`);
    }

    return tag.replace(/<img\b/i, `<img src="${sentinel}"`);
  });

  return { html: rewrittenHtml, keys };
}

function cloneBlockEditorData(content: BlockEditorData): BlockEditorData {
  if (typeof structuredClone === "function") {
    return structuredClone(content);
  }

  return JSON.parse(JSON.stringify(content)) as BlockEditorData;
}
